import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'

import {
  Box, Button, FormField, LoadingBubbles, SidebarModal, FileUploadZone, Text,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'

import { getUppyOptions, getComponentCallbacks, getRequestOptions } from '@functions/uploadModal'
import { assetTypesForServiceGroup, I_GUIDE_URL_ASSET_TYPE_ID } from '@functions/assetType'

import useUppyUpload from '@hooks/useUppyUpload'
import useCurrentUser from '@hooks/useCurrentUser'
import useReduxAction from '@hooks/useReduxAction'

import userTypes from '@enums/userTypes'
import prestigeServices from '@enums/prestigeServices'

import FailedItemList from './components/FailedItemList'
import RecentlyUploadedList from './components/RecentlyUploadedList'
import ResourcePrompts from './components/ResourcePrompts'

const defaultState = {
  allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/DNG', 'image/CR2', 'image/ARW'],
  allowUpload: false,
  assetTypeOptions: [],
  creatingDirect: false,
  directAssetName: '',
  directUrl: '',
  doneUpload: false,
  filesToUploadCount: 0,
  filteredFileTypes: [],
  selectedAssetType: '',
  selectedFileType: 'image',
  recentlyFailed: [],
  recentlyUploaded: [],
  lastDeletedId: 0,
  lastFailedBatch: [],
  lastUploadedBatch: [],
  selectedResourceType: 0,
  showUploadNewBatch: false,
  uploadResourceType: null,
}

const assetFileTypes = {
  image: {
    id: 'image',
    description: 'Image',
    fileTypes: ['image/jpeg', 'image/jpg'],
  },
  pdf: {
    id: 'pdf',
    description: 'PDF',
    fileTypes: ['application/pdf'],
  },
  video: {
    id: 'video',
    description: 'Video',
    fileTypes: ['video/mp4'],
  },
}

const insertRecentlyUploadedBatch = (newUploadList, uploadList, newFailedList, failedList, setState) => {
  setState({ lastUploadedBatch: [], lastFailedBatch: [], doneUpload: false })
  setState({
    recentlyUploaded: uploadList.concat(newUploadList),
    recentlyFailed: failedList.concat(newFailedList),
  })
}

const removeRecentlyDeleted = (id, list, setState) => {
  setState({ lastDeletedId: 0 })
  setState({ recentlyUploaded: list.filter(x => x.id !== id) })
}

const UploadModal = (props) => {
  const {
    callbacks,
    modalKey,
    serviceJob,
    showModal,
    title,
    titleSecondLine,
    serviceJobOptions,
    type,
    fileTypesAllowed,
    resourceTypes,
  } = props

  const { createDirectFn, deleteFn, checkProgress, checkUploadStatus, scanFolderFn } = callbacks

  const entities = useSelector(reduxState => reduxState.entities)
  const { assets, assetTypes, resources, serviceGroups, fileServiceContainers } = entities

  const assetTypeList = Object.values(assetTypes || {})

  const { currentUser } = useCurrentUser()

  const [state, setState] = useSetState(defaultState)
  const {
    allowedFileTypes,
    allowUpload,
    assetTypeOptions,
    creatingDirect,
    directAssetName,
    directUrl,
    doneUpload,
    filteredFileTypes,
    filesToUploadCount,
    recentlyFailed,
    recentlyUploaded,
    lastDeletedId,
    lastFailedBatch,
    lastUploadedBatch,
    selectedAssetType,
    selectedResourceType,
    showUploadNewBatch,
  } = state

  const containerName = `service-jobs-${DateTime.fromISO(serviceJob.createdAt, { zone: 'utc' }).toFormat('yyyy')}`

  const { loading } = useReduxAction('fileServiceContainers', 'loadFileServiceContainer', containerName, [])
  const { loading: resourcesloading } = useSelector(reduxState => reduxState.resources)

  const fileServiceContainer = Object.values(fileServiceContainers).find(x => x.container === containerName)

  useReduxAction('serviceJobs', 'loadServiceJobV1', {
    includes: ['service', 'campaign', 'client', 'details', 'users', 'notes'].join(','),
  }, [serviceJob.id, doneUpload], {
    dispatchAction: (action, requestOptions) => action(serviceJob.id, requestOptions),
    shouldPerformFn: () => doneUpload && type === 'Resources',
  })

  useEffect(() => {
    if (type === 'Assets') setState({ allowUpload: true })
  }, [type])

  useEffect(() => {
    if (lastDeletedId > 0) {
      removeRecentlyDeleted(lastDeletedId, recentlyUploaded, setState)
    }
  }, [resources, assets])

  useEffect(() => {
    if (fileTypesAllowed) {
      setState({ allowedFileTypes: fileTypesAllowed.split(',') })
    }
  }, [fileTypesAllowed])

  useEffect(() => {
    if (doneUpload) {
      if (type === 'Resources') {
        setState({ allowUpload: false, showUploadNewBatch: true })
      }
      if (lastUploadedBatch.length > 0 || lastFailedBatch.length > 0) {
        insertRecentlyUploadedBatch(lastUploadedBatch, recentlyUploaded, lastFailedBatch, recentlyFailed, setState)
      }
    }
  }, [doneUpload])

  const uppyCallbacks = { ...getComponentCallbacks('uppy', setState), createDirectFn, scanFolderFn }
  const selectedAssetTypeId = assetTypeOptions.find(option => option.key === selectedAssetType)?.id
  const uppyOptions = getUppyOptions(type, allowedFileTypes, serviceJobOptions, uppyCallbacks, selectedResourceType, filesToUploadCount, fileServiceContainer, currentUser.id, selectedAssetType, selectedAssetTypeId)
  const uppy = useUppyUpload(uppyOptions)

  uppy.on('progress', (data) => {
    checkProgress(data)
  })

  useEffect(() => {
    checkUploadStatus(recentlyUploaded)
  }, [recentlyUploaded])

  const resetUploader = () => {
    setState({
      allowUpload: false,
      doneUpload: false,
      filesToUploadCount: 0,
      recentlyFailed: [],
      recentlyUploaded: [],
      lastDeletedId: 0,
      lastFailedBatch: [],
      lastUploadedBatch: [],
      selectedResourceType: resourceTypes ? Object.values(resourceTypes)[0]?.id : 0,
      showUploadNewBatch: false,
      uploadResourceType: resourceTypes ? Object.values(resourceTypes)[0]?.id : 0,
    })
  }

  const changeResourceType = (event) => {
    setState({ selectedResourceType: event.target.value, uploadResourceType: event.target.value })
  }

  // Virtual_Tour assets are a pasted direct URL (typed as IGuide Url) rather than an uploaded
  // file, so the create flow swaps the dropzone for a name + URL field and a button.
  const isVirtualTour = type === 'Assets' && serviceGroups[serviceJob.serviceGroupId]?.name === 'Virtual Tour'

  const createDirectAsset = async () => {
    const url = directUrl.trim()
    const name = directAssetName.trim()
    if (!url || !name || creatingDirect) return

    setState({ creatingDirect: true })
    const { success, errors } = await createDirectFn({
      ...getRequestOptions(type, serviceJobOptions, selectedResourceType, selectedAssetType, selectedAssetTypeId),
      // No name column on an asset — the backend's required `Description` is the display title.
      description: name,
      assetTypeId: I_GUIDE_URL_ASSET_TYPE_ID,
      directUrl: url,
    })
    setState({ creatingDirect: false })

    if (success) {
      setState({ directAssetName: '', directUrl: '' })
      callbacks.closeModal()
    } else {
      toast.warning(errors?.[0])
    }
  }

  useEffect(() => {
    if (resourceTypes) {
      if (prestigeServices.find(x => x.id === serviceJob.service?.id) && currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id) {
        setState({ selectedResourceType: resourceTypes.find(x => x.name === 'ClientGalleryImage').id })
      } else setState({ selectedResourceType: resourceTypes.find(x => x.name === 'Ambient')?.id || Object.values(resourceTypes)[0]?.id })
    }
  }, [resourceTypes])

  useEffect(() => {
    if (type === 'Assets') {
      const groupAssetTypes = assetTypesForServiceGroup(serviceGroups[serviceJob.serviceGroupId]?.name, assetTypeList)
      setState({ assetTypeOptions: groupAssetTypes, selectedAssetType: groupAssetTypes[0]?.key || '' })

      if (['Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)) {
        setState({ filteredFileTypes: Object.values(assetFileTypes).filter(fileType => fileType.id === 'pdf') })
      }
      if (['Photography', 'Virtual Staging'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)) {
        setState({ filteredFileTypes: Object.values(assetFileTypes).filter(fileType => fileType.id === 'image') })
      }
      if (['Video', 'VidMo'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)) {
        setState({ filteredFileTypes: Object.values(assetFileTypes).filter(fileType => fileType.id === 'video') })
      }
    }
  }, [serviceJob, assetTypeList.length])

  useEffect(() => {
    if (filteredFileTypes.length) {
      setState({
        allowedFileTypes: filteredFileTypes[0].fileTypes,
        selectedFileType: filteredFileTypes[0].id,
      })
    }
  }, [filteredFileTypes])

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header callbacks={callbacks} title={isVirtualTour ? 'Create' : title} titleSecondLine={isVirtualTour ? 'Asset' : titleSecondLine} />
      <SidebarModal.Content hasFooter={showUploadNewBatch}>
        <Box flexDirection="column" flexShrink={0}>
          {type === 'Resources' && (
            <ResourcePrompts
              callbacks={{
                allowResourceUpload: () => setState({ allowUpload: true }),
                updateUploadCount: x => setState({ filesToUploadCount: x }),
              }}
              uploadState={state}
            />
          )}
        </Box>

        {(loading || resourcesloading) && <LoadingBubbles color="rgba(0, 0, 0, 0.15)" style={{ flexShrink: 0 }} />}

        {!loading && allowUpload && (
          <Box flexDirection="column" marginBottom="large">
            {type === 'Assets' && !isVirtualTour && assetTypeOptions.length > 0 && (
              <FormField direction="column" label="Choose Type" marginTop="large" value={selectedAssetType}>
                <select value={selectedAssetType} onChange={e => setState({ selectedAssetType: e.target.value })}>
                  {assetTypeOptions.map(({ key, label }) => <option value={key} key={key}>{label}</option>)}
                </select>
              </FormField>
            )}
            {type === 'Resources' && resourceTypes?.length > 0 && (
              <FormField direction="column" label="Choose Resource Type" marginTop="large" value={selectedResourceType}>
                <select onChange={changeResourceType} value={selectedResourceType}>
                  {Object.values(resourceTypes).sort((a, b) => (a.name > b.name ? 1 : -1)).map(({ id, description }) => <option value={id} key={id}>{description}</option>)}
                </select>
              </FormField>
            )}
            {isVirtualTour && (
              <>
                <FormField direction="column" label="Name" marginTop="large" value={directAssetName}>
                  <input
                    type="text"
                    value={directAssetName}
                    maxLength={50}
                    onChange={e => setState({ directAssetName: e.target.value })}
                    placeholder="Name"
                  />
                </FormField>
                <FormField direction="column" label="Virtual Tour URL" marginTop="large" value={directUrl}>
                  <input
                    type="text"
                    value={directUrl}
                    onChange={e => setState({ directUrl: e.target.value })}
                    placeholder="Virtual Tour URL"
                  />
                </FormField>
                <Button
                  buttonStyle="primaryCreate"
                  disabled={!directAssetName.trim() || !directUrl.trim() || creatingDirect}
                  loading={creatingDirect}
                  marginTop="large"
                  onClick={createDirectAsset}
                  size="medium"
                >
                  Create Asset
                </Button>
              </>
            )}
            {!isVirtualTour && uppy && ((type === 'Resources' && resourceTypes?.length > 0) || type === 'Assets') && (
              <FormField direction="column" marginTop="large">
                <FileUploadZone
                  uppy={uppy}
                  subTitle="Max Size is 2 GB"
                />
              </FormField>
            )}
            {!isVirtualTour && !uppy && (
              <FormField direction="column" marginTop="large">
                <Text marginBottom="small" fontSize="xsmall">
                  Could not load uppy service
                </Text>
              </FormField>
            )}
          </Box>
        )}

        {recentlyFailed.length > 0 && (
          <SidebarModal.ExpandableSectionBox label={`Failed Items (${recentlyFailed.length})`}>
            <FailedItemList list={recentlyFailed} />
          </SidebarModal.ExpandableSectionBox>
        )}

        {recentlyUploaded.length > 0 && (
          <SidebarModal.ExpandableSectionBox label={`Uploaded Items (${recentlyUploaded.length})`}>
            <RecentlyUploadedList
              list={recentlyUploaded}
              callbacks={{ ...getComponentCallbacks('recentlyuploadedlist', setState), deleteFn }}
              fileType={type}
            />
          </SidebarModal.ExpandableSectionBox>
        )}
      </SidebarModal.Content>

      {showUploadNewBatch && (
        <SidebarModal.Footer>
          <Button
            buttonStyle="primaryCreate"
            onClick={() => resetUploader()}
            size="medium"
          >
            Upload New Batch
          </Button>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

UploadModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  serviceJobOptions: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
  serviceJob: PropTypes.object,
  title: PropTypes.string,
  titleSecondLine: PropTypes.string,
  resourceTypes: PropTypes.array,
  fileTypesAllowed: PropTypes.string,
}

UploadModal.defaultProps = {
  modalKey: 'UploadModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <UploadModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
